// src/components/lekhi/LekhiTodayPage.js
// NEW daily Lekhi: today screen (1–2 topic cards) → topic steps → score → reward.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getUserData,
  getIndiaDate,
  getIndiaDateString,
  getTodayLekhiCompletion,
  getLekhiPlanForDay,
  getLekhiDayTypeMinutes,
  getLekhiTopicContent,
  saveLekhiTopicDone,
  completeLekhiPlanDay,
  checkAndUnlockAchievements,
  getFAQs,
  getLekhiCycleTestAccuracy,
  startNextLekhiCycle,
  LEKHI_PLAN_DAYS
} from '../../services/firebase/firestoreService';
import RewardScreen from '../rewards/RewardScreen';
import { getSubjectName, getTopicNameMr } from '../../services/lekhiTopicNames';
import TextView from './TextView';
import AudioView from './AudioView';
import MCQs from './MCQs';
import { LekhiWatermark, lekhiProtectProps, LEKHI_PROTECT_CLASS } from './LekhiProtect';
import './LekhiTrainingPage.css';
import './LekhiTodayPage.css';

const SUPPORT_WHATSAPP = '919359246193';
const RESULT_SCREEN_MS = 5000;   // score screen stays this long
const REWARD_DELAY_MS = 3000;    // today screen (all green) before the reward
// Page container: keeps the page style AND adds watermark/copy protection
const CONTAINER_PROPS = {
  className: `lekhi-training-container ${LEKHI_PROTECT_CLASS}`,
  ...lekhiProtectProps
};

const FALLBACK_FAQS = {
  en: [
    { q: 'Audio is not playing. What should I do?', a: 'Check your phone volume and internet connection. If the problem continues, contact your admin.' },
    { q: 'Can I do Lekhi twice in a day?', a: 'No. One Lekhi day per date. You can revisit finished topics in the Library.' },
    { q: 'What if I close the app in the middle of a topic?', a: 'That topic restarts from the beginning next time. A topic you already finished stays saved.' },
    { q: 'What if I finish only one topic today?', a: 'It stays done. Next time you will see only the remaining topic. Points and streak count when both topics of the day are done.' }
  ],
  mr: [
    { q: 'ऑडिओ चालू होत नाही. काय करू?', a: 'फोनचा आवाज आणि इंटरनेट तपासा. समस्या कायम राहिल्यास admin शी संपर्क साधा.' },
    { q: 'एका दिवसात दोनदा लेखी करता येते का?', a: 'नाही. एका तारखेला एकच लेखी दिवस. पूर्ण झालेले विषय Library मध्ये पुन्हा पाहता येतील.' },
    { q: 'विषय मधेच सोडून अ‍ॅप बंद झाले तर?', a: 'तो विषय पुढच्या वेळी सुरुवातीपासून करावा लागेल. आधी पूर्ण केलेला विषय जतन राहतो.' },
    { q: 'आज एकच विषय पूर्ण केला तर?', a: 'तो पूर्णच राहतो. पुढच्या वेळी फक्त उरलेला विषय दिसेल. दोन्ही विषय पूर्ण झाल्यावरच गुण आणि स्ट्रीक मिळतात.' }
  ]
};

// ---------- Level picker (after Day 60) ----------
const PICKER_SUBJECTS = ['grammar', 'math', 'reasoning', 'gk'];
const LEVEL_ORDER = ['easy', 'medium', 'hard'];
const LEVEL_NAMES = {
  mr: { easy: 'प्रारंभ', medium: 'प्रगत', hard: 'प्रावीण्य' },
  en: { easy: 'Easy', medium: 'Medium', hard: 'Hard' }
};
const SUGGESTED_LEVEL = { easy: 'medium', medium: 'hard', hard: 'medium' };
const PICKER_TEXT = {
  mr: {
    title: '६० दिवस पूर्ण!',
    sub: 'सर्व विषयांचा अभ्यास आणि उजळणी पूर्ण झाली. आता पुढच्या ६० दिवसांसाठी प्रत्येक विषयाचा स्तर निवड.',
    test: 'रविवार परीक्षा', correct: 'बरोबर', noTests: '—',
    current: 'सध्याचा', suggested: 'सुचवलेला',
    lowerNote: 'खालचा स्तर निवडल्याने पाया अधिक पक्का होईल. तयार झालास की पुढच्या वेळी पुन्हा वरचा स्तर निवडता येईल.',
    start: 'पुढचे ६० दिवस सुरू करा',
    confirm: 'निवडलेल्या स्तरांसह पुढचे ६० दिवस सुरू करायचे?',
    saving: 'सुरू होत आहे...',
    error: 'सुरू करता आले नाही. इंटरनेट तपासा आणि पुन्हा प्रयत्न करा.'
  },
  en: {
    title: '60 days complete!',
    sub: 'You have studied and revised every topic. Now choose a level for each subject for the next 60 days.',
    test: 'Sunday tests', correct: 'correct', noTests: '—',
    current: 'Current', suggested: 'Suggested',
    lowerNote: 'Choosing a lower level will make your basics stronger. When you are ready, you can choose a higher level next time.',
    start: 'Start the next 60 days',
    confirm: 'Start the next 60 days with the chosen levels?',
    saving: 'Starting...',
    error: 'Could not start. Check your internet and try again.'
  }
};

// Same WhatsApp icon as the login screen
const WhatsAppIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="white" width={size} height={size}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ---------- Small shared pieces (same look as the old Lekhi page) ----------
const DonutProgress = ({ completed, total }) => {
  const size = 72;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? (completed / total) * circumference : 0;
  const offset = circumference - progress;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgb(255, 255, 255)" strokeWidth={strokeWidth}/>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#28a745" strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 0.4s ease' }}/>
    </svg>
  );
};

const FAQModal = ({ language, faqs, onClose }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="faq-overlay" onClick={onClose}>
      <div className="faq-modal" onClick={e => e.stopPropagation()}>
        <div className="faq-header">
          <span className="faq-title">{language === 'mr' ? 'सामान्य प्रश्न' : 'FAQs'}</span>
          <button className="faq-close" onClick={onClose}>✕</button>
        </div>
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <div key={i} className="faq-item">
              <button
                className={`faq-question ${openIndex === i ? 'open' : ''}`}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span>{faq.q}</span>
                <span className="faq-arrow">{openIndex === i ? '▲' : '▼'}</span>
              </button>
              {openIndex === i && <div className="faq-answer">{faq.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ConfirmBackModal = ({ language, onConfirm, onCancel }) => {
  const text = language === 'mr'
    ? { msg: 'या विषयाची प्रगती हरवेल आणि विषय पुन्हा सुरुवातीपासून करावा लागेल. खरोखर मागे जायचे?', yes: 'होय, मागे जा', no: 'नाही, सुरू ठेवा' }
    : { msg: "This topic's progress will be lost and it will restart from the beginning. Go back?", yes: 'Yes, go back', no: 'No, continue' };
  return (
    <div className="confirm-overlay" onClick={onCancel}>
      <div className="confirm-modal" onClick={e => e.stopPropagation()}>
        <div className="confirm-message">{text.msg}</div>
        <div className="confirm-actions">
          <button className="confirm-cancel" onClick={onCancel}>{text.no}</button>
          <button className="confirm-yes" onClick={onConfirm}>{text.yes}</button>
        </div>
      </div>
    </div>
  );
};

// ---------- Content helpers ----------
const isContentReady = (dayType, c) => {
  if (!c) return false;
  const mcqOk = Array.isArray(c.mcqs) && c.mcqs.length === 30;
  if (dayType === 'study') return !!(c.audio1URL && c.audio2URL && c.studyText && mcqOk);
  return !!(c.revisionText && c.cheatSheet && mcqOk);
};

const sliceMcqs = (content, from, to, idPrefix) =>
  (content?.mcqs || [])
    .filter(q => q.n >= from && q.n <= to)
    .sort((a, b) => a.n - b.n)
    .map(q => ({ ...q, id: `${idPrefix}_q${q.n}` }));

const buildTopicBlocks = (dayType, content, isMr, idPrefix) => {
  const q = isMr ? 'प्रश्न' : 'questions';
  if (dayType === 'study') {
    const m1 = sliceMcqs(content, 1, 5, idPrefix);
    const m2 = sliceMcqs(content, 6, 10, idPrefix);
    return [
      { id: 'audio1', icon: '/icon-audio.svg', type: 'audio', title: isMr ? 'शिकवणी' : 'Teaching', subtitle: isMr ? 'ऑडिओ १' : 'Audio 1', data: { audioURL: content?.audio1URL } },
      { id: 'studyText', icon: '/icon-study.svg', type: 'text', title: isMr ? 'पाठ्यपुस्तक' : 'Study text', subtitle: isMr ? 'वाचा' : 'Read', data: { text: content?.studyText || '' } },
      { id: 'mcq1', icon: '/icon-mcq.svg', type: 'mcq', title: isMr ? 'MCQs भाग १' : 'MCQs Part 1', subtitle: `${m1.length} ${q}`, data: m1 },
      { id: 'audio2', icon: '/icon-audio.svg', type: 'audio', title: isMr ? 'चुका टाळा' : 'Mistakes Drill', subtitle: isMr ? 'ऑडिओ २' : 'Audio 2', data: { audioURL: content?.audio2URL } },
      { id: 'mcq2', icon: '/icon-mcq.svg', type: 'mcq', title: isMr ? 'MCQs भाग २' : 'MCQs Part 2', subtitle: `${m2.length} ${q}`, data: m2 }
    ];
  }
  const r1 = sliceMcqs(content, 11, 20, idPrefix);
  const r2 = sliceMcqs(content, 21, 30, idPrefix);
  return [
    { id: 'revisionText', icon: '/icon-revision.svg', type: 'text', title: isMr ? 'उजळणी पाठ' : 'Revision text', subtitle: isMr ? 'वाचा' : 'Read', data: { text: content?.revisionText || '' } },
    { id: 'mcq1', icon: '/icon-mcq.svg', type: 'mcq', title: isMr ? 'MCQs भाग १' : 'MCQs Part 1', subtitle: `${r1.length} ${q}`, data: r1 },
    { id: 'cheatSheet', icon: '/icon-cheatsheet.svg', type: 'text', title: isMr ? 'झटपट उजळणी' : 'Cheat sheet', subtitle: isMr ? 'वाचा' : 'Read', data: { text: content?.cheatSheet || '' } },
    { id: 'mcq2', icon: '/icon-mcq.svg', type: 'mcq', title: isMr ? 'MCQs भाग २' : 'MCQs Part 2', subtitle: `${r2.length} ${q}`, data: r2 }
  ];
};

const LekhiTodayPage = ({ onBack }) => {
  const { currentUser, userData, refreshUserData } = useAuth();
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const isMr = language === 'mr';

  // Page data
  const [view, setView] = useState('loading'); // loading | today | alreadyDone | cycleComplete | error
  const [planDay, setPlanDay] = useState(1);
  const [plan, setPlan] = useState(null);
  const [progressTopics, setProgressTopics] = useState({});
  const [levels, setLevels] = useState({});
  const [contents, setContents] = useState({});

  // Day state
  const [dayLocked, setDayLocked] = useState(false);
  const [dayRecorded, setDayRecorded] = useState(false);
  const [dayCompleteError, setDayCompleteError] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [justDone, setJustDone] = useState(null);

  // Topic state
  const [activeSubject, setActiveSubject] = useState(null);
  const [topicDone, setTopicDone] = useState(new Set());
  const [activeBlockId, setActiveBlockId] = useState(null);
  const [dotState, setDotState] = useState([]);
  const [topicSaving, setTopicSaving] = useState(false);
  const [topicSaveError, setTopicSaveError] = useState(false);
  const [result, setResult] = useState(null);

  // UI
  const [showConfirmBack, setShowConfirmBack] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [faqList, setFaqList] = useState(FALLBACK_FAQS[language] || FALLBACK_FAQS.mr);
  const [picker, setPicker] = useState(null); // level picker after Day 60

  const scoreRef = useRef({ correct: 0, wrong: 0 });
  const blockSnapshotRef = useRef(null);
  const topicSavingRef = useRef(false);
  const dayCompletingRef = useRef(false);

  const t = isMr
    ? {
        back: '← मागे', day: 'दिवस', loading: 'तुमची लेखी लोड होत आहे...',
        todayTopics: 'आजचे विषय', study: 'अभ्यास', revision: 'उजळणी',
        studyDay: 'अभ्यास दिवस', revisionDay: 'उजळणी दिवस', minutes: 'मिनिटे',
        notReady: 'हा विषय अजून तयार नाही', contactAdmin: 'सहाय्यता संपर्क',
        start: 'सुरू करा', done: '✓ पूर्ण', todayTopic: 'आजचा विषय',
        correct: 'बरोबर उत्तरे', saving: 'जतन होत आहे...',
        saveError: 'विषय जतन करता आला नाही. इंटरनेट तपासा.', retry: 'पुन्हा प्रयत्न करा',
        dayDoneBanner: '✓ आजची लेखी पूर्ण!', dayError: 'दिवस नोंदवता आला नाही. इंटरनेट तपासा.',
        alreadyTitle: 'आजची लेखी पूर्ण झाली आहे', alreadySub: 'उद्या पुन्हा भेटू!', goHome: 'पुढे जा',
        errorTitle: 'लेखी लोड करता आली नाही', errorSub: 'इंटरनेट तपासा आणि पुन्हा प्रयत्न करा.'
      }
    : {
        back: '← Back', day: 'Day', loading: 'Loading your lekhi...',
        todayTopics: "Today's topics", study: 'Study', revision: 'Revision',
        studyDay: 'Study day', revisionDay: 'Revision day', minutes: 'min',
        notReady: 'This topic is not ready yet', contactAdmin: 'Contact Support',
        start: 'Start', done: '✓ Done', todayTopic: "Today's topic",
        correct: 'correct answers', saving: 'Saving...',
        saveError: 'Could not save the topic. Check your internet.', retry: 'Try again',
        dayDoneBanner: "✓ Today's Lekhi is complete!", dayError: 'Could not record the day. Check your internet.',
        alreadyTitle: "Today's Lekhi is already complete", alreadySub: 'See you tomorrow!', goHome: 'Go Home',
        errorTitle: 'Could not load Lekhi', errorSub: 'Check your internet and try again.'
      };

  const india = getIndiaDate();
  const days = isMr
    ? ['रविवार','सोमवार','मंगळवार','बुधवार','गुरुवार','शुक्रवार','शनिवार']
    : ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const months = isMr
    ? ['जाने','फेब्रु','मार्च','एप्रिल','मे','जून','जुलै','ऑग','सप्टें','ऑक्टो','नोव्हें','डिसें']
    : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dateString = `${days[india.getDay()]}, ${months[india.getMonth()]} ${india.getDate()}`;
  const firstName = userData?.firstName || '';
  const todayStr = getIndiaDateString();
  // Instant lock: known the moment the profile is loaded — no database wait.
  const instantDone = userData?.lastLekhiCompletedDate === todayStr;

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
    setFaqList(FALLBACK_FAQS[newLang]);
  };

  // ---------- Day completion ----------
  const finishDay = useCallback(async () => {
    if (!currentUser?.uid || dayCompletingRef.current) return;
    dayCompletingRef.current = true;
    setDayLocked(true);
    setDayCompleteError(false);
    try {
      await completeLekhiPlanDay(currentUser.uid);
      setDayRecorded(true);
    } catch (e) {
      dayCompletingRef.current = false;
      setDayCompleteError(true);
    }
  }, [currentUser]);

  // ---------- Load today ----------
  const loadPage = useCallback(async () => {
    if (!currentUser?.uid) return;
    setView('loading');
    setActiveSubject(null);
    setActiveBlockId(null);
    setResult(null);
    setJustDone(null);
    setDayLocked(false);
    setDayRecorded(false);
    setDayCompleteError(false);
    dayCompletingRef.current = false;
    try {
      const today = getIndiaDateString();
      const fresh = await getUserData(currentUser.uid);
      if (!fresh) throw new Error('User not found.');
      if (fresh.lastLekhiCompletedDate === today) { setView('alreadyDone'); return; }

      // Second opinion — fails OPEN so a network blip never locks a genuine user out.
      let completionExists = false;
      try {
        completionExists = !!(await getTodayLekhiCompletion(currentUser.uid));
      } catch (e) {
        completionExists = false;
      }
      if (completionExists) { setView('alreadyDone'); return; }

      const day = fresh.lekhiPlanDay || 1;
      setPlanDay(day);
      if (day > LEKHI_PLAN_DAYS) {
        const current = {};
        PICKER_SUBJECTS.forEach(s => { current[s] = fresh[`${s}Level`] || 'easy'; });
        setPicker({ current, chosen: { ...current }, accuracy: null, saving: false, error: false });
        setView('cycleComplete');
        getLekhiCycleTestAccuracy(currentUser.uid, fresh.lekhiCycleStartDate || null)
          .then(acc => setPicker(p => (p ? { ...p, accuracy: acc } : p)))
          .catch(() => setPicker(p => (p ? { ...p, accuracy: { grammar: null, math: null, reasoning: null, gk: null } } : p)));
        return;
      }

      const p = getLekhiPlanForDay(day);
      const prog = (fresh.lekhiDayProgress && fresh.lekhiDayProgress.day === day)
        ? (fresh.lekhiDayProgress.topics || {})
        : {};
      const lv = {};
      const ct = {};
      await Promise.all(p.topics.map(async tp => {
        const level = fresh[`${tp.subject}Level`] || null;
        lv[tp.subject] = level;
        if (!level) { ct[tp.subject] = null; return; }
        try {
          ct[tp.subject] = await getLekhiTopicContent(tp.subject, tp.topic, level);
        } catch (e) {
          ct[tp.subject] = null;
        }
      }));

      setPlan(p);
      setProgressTopics(prog);
      setLevels(lv);
      setContents(ct);
      setView('today');

      // Recovery: all topics were saved earlier but the day was not recorded (e.g. network drop).
      if (p.topics.length > 0 && p.topics.every(tp => prog[tp.subject])) {
        finishDay();
      }
    } catch (e) {
      setView('error');
    }
  }, [currentUser, finishDay]);

  useEffect(() => { loadPage(); }, [loadPage]);

  // Score screen: stays for 5 seconds, then back to the today screen (card turns green)
  useEffect(() => {
    if (!result) return undefined;
    const subject = result.subject;
    const id = setTimeout(() => {
      setResult(null);
      setActiveBlockId(null);
      setActiveSubject(null);
      setTopicDone(new Set());
      setDotState([]);
      setJustDone(subject);
    }, RESULT_SCREEN_MS);
    return () => clearTimeout(id);
  }, [result]);

  // Small "pop" animation on the card that just turned green
  useEffect(() => {
    if (!justDone) return undefined;
    const id = setTimeout(() => setJustDone(null), 1500);
    return () => clearTimeout(id);
  }, [justDone]);

  // Reward appears only after the student is back on the today screen with all cards green
  useEffect(() => {
    if (!dayRecorded || activeSubject || result || showReward) return undefined;
    const id = setTimeout(() => setShowReward(true), REWARD_DELAY_MS);
    return () => clearTimeout(id);
  }, [dayRecorded, activeSubject, result, showReward]);

  // ---------- Topic flow ----------
  const activeTopic = plan?.topics.find(tp => tp.subject === activeSubject) || null;
  const topicBlocks = activeTopic
    ? buildTopicBlocks(
        plan.dayType,
        contents[activeSubject],
        isMr,
        `${activeSubject}_${activeTopic.topic}_${levels[activeSubject]}`
      )
    : [];
  const topicTotalDots = topicBlocks
    .filter(b => b.type === 'mcq')
    .reduce((sum, b) => sum + b.data.length, 0);

  const openTopic = (subject) => {
    if (dayLocked || !plan) return;
    if (progressTopics[subject]) return;
    if (!isContentReady(plan.dayType, contents[subject])) return;
    scoreRef.current = { correct: 0, wrong: 0 };
    blockSnapshotRef.current = null;
    setDotState([]);
    setTopicDone(new Set());
    setActiveBlockId(null);
    setTopicSaveError(false);
    setActiveSubject(subject);
  };

  const closeTopic = () => {
    setShowConfirmBack(false);
    setActiveBlockId(null);
    setActiveSubject(null);
    setTopicDone(new Set());
    setDotState([]);
    setTopicSaveError(false);
  };

  const recordItemResult = (itemId, correct) => {
    if (correct) scoreRef.current.correct++;
    else scoreRef.current.wrong++;
    setDotState(prev => [...prev, correct]);
  };

  const handleBlockTap = (block, index) => {
    if (topicDone.has(block.id)) return;
    const first = topicBlocks.findIndex(b => !topicDone.has(b.id));
    if (index !== first) return;
    blockSnapshotRef.current = {
      correct: scoreRef.current.correct,
      wrong: scoreRef.current.wrong,
      dots: dotState.length
    };
    setActiveBlockId(block.id);
  };

  // Leaving a block early undoes any answers given inside it (no double counting)
  const handleBlockBack = () => {
    const s = blockSnapshotRef.current;
    if (s) {
      scoreRef.current = { correct: s.correct, wrong: s.wrong };
      setDotState(prev => prev.slice(0, s.dots));
    }
    setActiveBlockId(null);
  };

  const saveTopic = async () => {
    if (topicSavingRef.current || !activeTopic) return;
    topicSavingRef.current = true;
    setTopicSaving(true);
    setTopicSaveError(false);
    const subject = activeTopic.subject;
    const { correct, wrong } = scoreRef.current;
    const total = correct + wrong;
    try {
      const res = await saveLekhiTopicDone(currentUser.uid, {
        day: planDay,
        subject,
        topic: activeTopic.topic,
        level: levels[subject],
        correct,
        total
      });
      if (res.status === 'dayAlreadyDoneToday') { setView('alreadyDone'); closeTopic(); return; }
      if (res.status === 'stale' || res.status === 'cycleComplete') { await loadPage(); return; }

      const newProg = res.progress?.topics || { ...progressTopics, [subject]: { topic: activeTopic.topic, correct, total } };
      setProgressTopics(newProg);
      setResult({ subject, topic: activeTopic.topic, correct, total });

      const allDone = plan.topics.every(tp => newProg[tp.subject]);
      if (allDone) finishDay(); // runs in the background; screen locks immediately
    } catch (e) {
      setTopicSaveError(true);
    } finally {
      topicSavingRef.current = false;
      setTopicSaving(false);
    }
  };

  const handleBlockComplete = () => {
    if (!activeBlockId) return;
    const next = new Set(topicDone);
    next.add(activeBlockId);
    setTopicDone(next);
    setActiveBlockId(null);
    blockSnapshotRef.current = null;
    if (next.size >= topicBlocks.length) saveTopic();
  };

  const handleTopicBackTap = () => {
    if (topicSaving) return;
    if (topicDone.size === 0) { closeTopic(); return; }
    setShowConfirmBack(true);
  };

  // ---------- Misc handlers ----------
  const handleTodayBack = () => {
    onBack(dayRecorded);
  };

  const handleFAQOpen = async () => {
    try {
      const all = await getFAQs();
      const active = all
        .filter(f => f.active === true)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (active.length > 0) {
        setFaqList(active.map(f => ({
          q: isMr ? f.questionMr : f.questionEn,
          a: isMr ? f.answerMr : f.answerEn
        })));
      }
    } catch (e) {}
    setShowFAQ(true);
  };

  const handleWhatsApp = (tp) => {
    const where = tp ? `${getSubjectName(tp.subject, 'mr')} - ${getTopicNameMr(tp.topic)}` : '';
    const message = isMr
      ? `नमस्कार, माझी लेखी चालू होत नाही. लेखी दिवस ${planDay}. ${where}`
      : `Hi, my Lekhi is not working. Lekhi Day ${planDay}. ${where}`;
    window.open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleRewardComplete = async () => {
    setShowReward(false);
    try {
      const newlyUnlocked = await checkAndUnlockAchievements(currentUser.uid);
      if (newlyUnlocked.length > 0) {
        const key = `newAchievements_${currentUser.uid}`;
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        localStorage.setItem(key, JSON.stringify([...existing, ...newlyUnlocked.map(a => a.id)]));

        const celebrateOnes = newlyUnlocked.filter(a => a.celebrate === true);
        if (celebrateOnes.length > 0) {
          const celKey = `celebrateAchievements_${currentUser.uid}`;
          const existingCel = JSON.parse(localStorage.getItem(celKey) || '[]');
          const toStore = celebrateOnes.map(a => ({
            id: a.id, category: a.category, title: a.title, description: a.description
          }));
          localStorage.setItem(celKey, JSON.stringify([...existingCel, ...toStore]));
        }
      }
      if (refreshUserData) await refreshUserData();
    } catch (e) {}
    onBack(true);
  };

  const handleStartNext = async () => {
    if (!picker || picker.saving) return;
    const P = isMr ? PICKER_TEXT.mr : PICKER_TEXT.en;
    if (!window.confirm(P.confirm)) return;
    setPicker(p => ({ ...p, saving: true, error: false }));
    try {
      await startNextLekhiCycle(currentUser.uid, picker.chosen);
      if (refreshUserData) await refreshUserData();
      setPicker(null);
      await loadPage();
    } catch (e) {
      setPicker(p => (p ? { ...p, saving: false, error: true } : p));
    }
  };

  // ---------- Header (identical to the old Lekhi page) ----------
  const renderHeader = (completed, total, onBackTap) => (
    <div className="lk-header">
      <div className="lk-header-top">
        <button className="back-btn" onClick={onBackTap}>{t.back}</button>
        <button className="lang-toggle-lk" onClick={handleLanguageToggle}>
          {language === 'en' ? 'मराठी' : 'EN'}
        </button>
      </div>
      <div className="lk-header-center">
        <DonutProgress completed={completed} total={total} />
        <span className="lk-progress-text">{completed}/{total}</span>
      </div>
      <div className="lk-header-bottom">
        <div className="lk-left">
          <div className="lk-user-name">{firstName}</div>
          <div className="lk-date">{dateString}</div>
        </div>
        <div className="lk-day-badge">{t.day} {Math.min(planDay, LEKHI_PLAN_DAYS)} / {LEKHI_PLAN_DAYS}</div>
      </div>
    </div>
  );

  const renderStateScreen = (icon, title, sub, buttons) => (
    <div {...CONTAINER_PROPS}>
      <LekhiWatermark />
      <div className="lk-block-list lk-above-watermark">
        <div className="lk-state-message">
          <div className="lk-state-icon">{icon}</div>
          <p className="lk-state-title">{title}</p>
          {sub && <p className="lk-state-sub">{sub}</p>}
          {buttons}
        </div>
      </div>
    </div>
  );

  // ================= RENDER =================
  if (showReward) {
    return (
      <RewardScreen
        userId={currentUser.uid}
        userData={userData}
        language={language}
        preloadedData={null}
        onComplete={handleRewardComplete}
      />
    );
  }

  if ((instantDone && !dayRecorded && !dayLocked) || view === 'alreadyDone') {
    return renderStateScreen('✅', t.alreadyTitle, t.alreadySub, (
      <button className="lkt-primary-btn" onClick={() => onBack(true)}>{t.goHome}</button>
    ));
  }

  if (view === 'loading') {
    return (
      <div className="lekhi-training-container">
        <div className="lk-block-list">
          <div className="lk-state-message">
            <div className="lk-spinner" />
            <p>{t.loading}</p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'error') {
    return renderStateScreen('📋', t.errorTitle, t.errorSub, (
      <div className="lkt-state-actions">
        <button className="lkt-primary-btn" onClick={loadPage}>{t.retry}</button>
        <button className="lkt-wa-icon" title={t.contactAdmin} aria-label={t.contactAdmin}
          onClick={() => handleWhatsApp(null)}>
          <WhatsAppIcon size={24} />
        </button>
      </div>
    ));
  }

  if (view === 'cycleComplete' && picker) {
    const P = isMr ? PICKER_TEXT.mr : PICKER_TEXT.en;
    const levelNames = isMr ? LEVEL_NAMES.mr : LEVEL_NAMES.en;
    return (
      <div {...CONTAINER_PROPS}>
        <LekhiWatermark />
        <div className="lkt-picker lk-above-watermark">
          <div className="lkt-picker-top">
            <button className="lkt-back-link" onClick={() => onBack(false)}>{t.back}</button>
          </div>
          <div className="lkt-picker-hero">
            <div className="lkt-picker-emoji">🎉</div>
            <div className="lkt-picker-title">{P.title}</div>
            <div className="lkt-picker-sub">{P.sub}</div>
          </div>

          {PICKER_SUBJECTS.map(s => {
            const current = picker.current[s];
            const chosen = picker.chosen[s];
            const suggested = SUGGESTED_LEVEL[current];
            const acc = picker.accuracy ? picker.accuracy[s] : undefined;
            const lower = LEVEL_ORDER.indexOf(chosen) < LEVEL_ORDER.indexOf(current);
            return (
              <div key={s} className="lkt-pick-card">
                <div className="lkt-pick-head">
                  <span className="lkt-pick-subject">{getSubjectName(s, language)}</span>
                  <span className="lkt-pick-acc">
                    {P.test}: {acc === undefined ? '...' : acc === null ? P.noTests : `${acc}% ${P.correct}`}
                  </span>
                </div>
                <div className="lkt-pick-tiles">
                  {LEVEL_ORDER.map(l => (
                    <button
                      key={l}
                      className={`lkt-pick-tile ${chosen === l ? 'selected' : ''}`}
                      onClick={() => setPicker(p => ({ ...p, chosen: { ...p.chosen, [s]: l }, error: false }))}
                      disabled={picker.saving}
                    >
                      {l === suggested && <span className="lkt-pick-star">⭐ {P.suggested}</span>}
                      <span className="lkt-pick-level">{levelNames[l]}</span>
                      {l === current && <span className="lkt-pick-current">{P.current}</span>}
                    </button>
                  ))}
                </div>
                {lower && <div className="lkt-pick-note">{P.lowerNote}</div>}
              </div>
            );
          })}

          {picker.error && <div className="lkt-error-box"><div>{P.error}</div></div>}

          <button className="lkt-primary-btn lkt-pick-start" onClick={handleStartNext} disabled={picker.saving}>
            {picker.saving ? P.saving : P.start}
          </button>
        </div>
      </div>
    );
  }

  // ----- Topic result (score) — green, no button, closes itself after 5 seconds -----
  if (result) {
    return (
      <div {...CONTAINER_PROPS}>
        <LekhiWatermark />
        <div className="lkt-result lk-above-watermark">
          <div className="lkt-result-topic">
            {getSubjectName(result.subject, language)} · {getTopicNameMr(result.topic)}
          </div>
          <div className="lkt-result-circle">
            <span className="lkt-result-score">{result.correct}/{result.total}</span>
          </div>
          <div className="lkt-result-label">{t.correct}</div>
        </div>
      </div>
    );
  }

  // ----- Inside a block (audio / text / MCQs) -----
  if (activeTopic && activeBlockId) {
    const block = topicBlocks.find(b => b.id === activeBlockId);
    const common = { language, onComplete: handleBlockComplete, onBack: handleBlockBack };
    if (block?.type === 'text') {
      return <TextView textContent={block.data} {...common} />;
    }
    if (block?.type === 'audio') {
      return (
        <AudioView
          locked
          audio={block.data}
          blockLabel={block.title}
          topicName={getTopicNameMr(activeTopic.topic)}
          {...common}
        />
      );
    }
    if (block?.type === 'mcq') {
      return (
        <MCQs
          mcqs={block.data}
          recordItemResult={recordItemResult}
          dotState={dotState}
          totalDots={topicTotalDots}
          {...common}
        />
      );
    }
  }

  // ----- Topic step list -----
  if (activeTopic) {
    const firstOpen = topicBlocks.findIndex(b => !topicDone.has(b.id));
    return (
      <div {...CONTAINER_PROPS}>
        <LekhiWatermark />
        {renderHeader(topicDone.size, topicBlocks.length, handleTopicBackTap)}

        <div className="lk-topic-band lk-above-watermark">
          <div className="lk-topic-band-label">
            {t.todayTopic} · {plan.dayType === 'study' ? t.study : t.revision}
          </div>
          <div className="lk-topic-band-value">
            {getSubjectName(activeTopic.subject, language)} - {getTopicNameMr(activeTopic.topic)}
          </div>
        </div>

        <div className="lk-block-list lk-above-watermark">
          {topicBlocks.map((block, i) => {
            const isCompleted = topicDone.has(block.id);
            const isActive = i === firstOpen;
            const isLocked = !isCompleted && !isActive;
            const iconSrc = isLocked ? '/icon-lock.svg' : block.icon;
            return (
              <div key={block.id} className={`lk-block-card ${isCompleted ? 'completed' : ''} ${isLocked ? 'locked' : ''}`}>
                <div className="lk-block-header">
                  <div className="lk-block-icon-wrap">
                    <img src={iconSrc} alt="" className="lk-block-icon-img" />
                  </div>
                  <div className="lk-block-info">
                    <div className="lk-block-name">{block.title}</div>
                    <div className="lk-block-subtitle">{block.subtitle}</div>
                  </div>
                </div>
                <div className="lk-block-controls">
                  {isCompleted ? (
                    <div className="lk-completed-indicator">{t.done}</div>
                  ) : isLocked ? null : (
                    <button className="lk-start-btn" onClick={() => handleBlockTap(block, i)}>
                      {t.start}
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {topicSaving && <div className="lkt-info-box">{t.saving}</div>}
          {topicSaveError && (
            <div className="lkt-error-box">
              <div>{t.saveError}</div>
              <button className="lkt-primary-btn" onClick={saveTopic}>{t.retry}</button>
            </div>
          )}
        </div>

        {showConfirmBack && (
          <ConfirmBackModal language={language} onConfirm={closeTopic} onCancel={() => setShowConfirmBack(false)} />
        )}
      </div>
    );
  }

  // ----- Today screen -----
  const doneCount = plan ? plan.topics.filter(tp => progressTopics[tp.subject]).length : 0;
  const totalTopics = plan ? plan.topics.length : 0;
  const minutes = plan ? getLekhiDayTypeMinutes(plan.dayType) : 0;
  const typeLabel = plan?.dayType === 'study' ? t.study : t.revision;

  return (
    <div {...CONTAINER_PROPS}>
      <LekhiWatermark />
      {renderHeader(doneCount, totalTopics, handleTodayBack)}

      <div className="lk-topic-band lk-above-watermark">
        <div className="lk-topic-band-label">{t.todayTopics}</div>
        <div className="lk-topic-band-value">
          {plan?.dayType === 'study' ? t.studyDay : t.revisionDay}
        </div>
      </div>

      <div className="lk-block-list lk-above-watermark">
        {dayLocked && !dayCompleteError && (
          <div className="lkt-done-banner">{t.dayDoneBanner}</div>
        )}
        {dayCompleteError && (
          <div className="lkt-error-box">
            <div>{t.dayError}</div>
            <button className="lkt-primary-btn" onClick={finishDay}>{t.retry}</button>
          </div>
        )}

        {plan && plan.topics.map(tp => {
          const done = progressTopics[tp.subject];
          const ready = isContentReady(plan.dayType, contents[tp.subject]);
          const tappable = !done && ready && !dayLocked;
          const cls = done ? 'done' : !ready ? 'notready' : '';
          return (
            <div
              key={tp.subject}
              className={`lkt-card ${cls} ${justDone === tp.subject ? 'just-done' : ''} ${tappable ? 'tappable' : ''}`}
              onClick={() => { if (tappable) openTopic(tp.subject); }}
            >
              <div className="lkt-card-subject">{getSubjectName(tp.subject, language)}</div>
              <div className="lkt-card-topic">{getTopicNameMr(tp.topic)}</div>
              <div className="lkt-card-meta">
                <span className="lkt-chip">{typeLabel}</span>
                {!done && ready && <span className="lkt-chip">⏱ ~{minutes} {t.minutes}</span>}
              </div>
              {done && <div className="lkt-card-status">✓ {done.correct}/{done.total}</div>}
              {tappable && <div className="lkt-card-arrow">→</div>}
              {!done && !ready && (
                <div className="lkt-card-notready">
                  <span>{t.notReady}</span>
                  <button className="lkt-wa-icon" title={t.contactAdmin} aria-label={t.contactAdmin}
                    onClick={(e) => { e.stopPropagation(); handleWhatsApp(tp); }}>
                    <WhatsAppIcon size={24} />
                  </button>
                </div>
              )}
            </div>
          );
        })}

        <button className="faq-trigger-btn" onClick={handleFAQOpen}>
          <div className="faq-trigger-icon">?</div>
        </button>
      </div>


      {showFAQ && <FAQModal language={language} faqs={faqList} onClose={() => setShowFAQ(false)} />}
    </div>
  );
};

export default LekhiTodayPage;